/**
 * ONE PLACE THAT TURNS A PICKED PHOTO INTO SOMETHING WE CAN STORE AND SEND.
 *
 * 🚨 THIS EXISTS BECAUSE FOUR SCREENS EACH HAD THEIR OWN COPY AND ONE OF THEM HAD BEEN
 * DEAD SINCE THE DAY IT WAS WRITTEN. The pickup and delivery checklists both called
 * `new Image()`, where `Image` is React Native's component imported at the top of those
 * files - it shadows the browser constructor, `new` on a forwardRef object throws, and the
 * surrounding catch quietly shipped the full-size original for months. A fifth copy is not
 * the answer to a bug that only existed because there were four.
 *
 * Two jobs, in order:
 *
 * 1. `toDataUri` - a picked `blob:` URI dies when iOS evicts and reloads the PWA, which it
 *    does when the camera opens. A `data:` URI survives, and uploads cleanly.
 *
 * 2. `shrinkForWeb` - the web picker IGNORES `quality`. It is an `<input type=file>`, so
 *    we get the camera roll original: a 12MP iPhone JPEG is 3-5MB and base64 adds a third.
 *    One photo is past both our 4.5MB progress snapshot AND Safari's ~5MB localStorage
 *    quota for the whole origin, so the snapshot that is supposed to carry the photo
 *    through that reload cannot hold it. A driver on a step wanting three photos can then
 *    never hold two at once - Shonrick Mcdaniel, 2026-09-10, job 215881, who could not get
 *    past the gear step and for whom "close the app and open it again" was useless advice,
 *    because closing the app does not clear localStorage.
 *
 * 1600px on the long edge at JPEG 0.7 is about 250KB: several persist comfortably, they
 * upload in seconds on a driveway connection, and it is still far more detail than "is
 * there a dolly in this picture" needs. Every failure path returns the original - a heavy
 * photo beats no photo.
 */
import { Platform } from 'react-native';

export const PHOTO_MAX_EDGE = 1600;
export const PHOTO_QUALITY = 0.7;

/** Read a picked URI into a self-contained data: URI. Returns the input unchanged on native. */
export async function toDataUri(uri: string): Promise<string> {
  if (Platform.OS !== 'web' || uri.startsWith('data:')) return uri;
  try {
    const blob = await (await fetch(uri)).blob();
    return await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result as string);
      r.onerror = () => reject(new Error('read failed'));
      r.readAsDataURL(blob);
    });
  } catch {
    return uri;
  }
}

/** Downscale a web data: URI. No-op on native, and on anything it cannot decode. */
export async function shrinkForWeb(
  dataUri: string,
  opts: { maxEdge?: number; quality?: number } = {},
): Promise<string> {
  if (Platform.OS !== 'web' || !dataUri.startsWith('data:image')) return dataUri;
  const maxEdge = opts.maxEdge ?? PHOTO_MAX_EDGE;
  const quality = opts.quality ?? PHOTO_QUALITY;
  try {
    const img = await new Promise<any>((resolve, reject) => {
      // 🚨 window.Image, NEVER a bare `new Image()`. See the note at the top of this file.
      const el = new (window as any).Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('decode failed'));
      el.src = dataUri;
    });
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return dataUri;
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    // Already small enough and already a JPEG: leave it alone rather than re-encoding it.
    if (scale === 1 && dataUri.startsWith('data:image/jpeg') && dataUri.length < 700_000) return dataUri;
    const canvas = (window as any).document.createElement('canvas');
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return dataUri;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const out = canvas.toDataURL('image/jpeg', quality);
    // A canvas that produced nothing usable (tainted, out of memory) must not replace a
    // real photo with a blank one.
    return typeof out === 'string' && out.length > 1000 ? out : dataUri;
  } catch {
    return dataUri;
  }
}

/** What every capture site should call: readable, reload-proof, and small enough to keep. */
export async function preparePhoto(
  uri: string,
  opts?: { maxEdge?: number; quality?: number },
): Promise<string> {
  return shrinkForWeb(await toDataUri(uri), opts);
}
